import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Mail, Lock, ArrowRight, CheckCircle, AlertCircle, AtSign, Shield, X, Send, ExternalLink } from 'lucide-react';
import { GoogleLogin } from '@react-oauth/google';
import { useTheme } from '../context/ThemeContext';

const MarsLoginForm = () => {
  const { isDark } = useTheme();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [redirectStep, setRedirectStep] = useState(0);
  const [focusedField, setFocusedField] = useState('');
  const [errors, setErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [floatingElements, setFloatingElements] = useState([]);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [etherealUrl, setEtherealUrl] = useState('');
  
  // Forgot Password Modal States
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [isForgotLoading, setIsForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [forgotFocusedField, setForgotFocusedField] = useState('');

  // Rolling text messages
  const rollingMessages = [
    'Gathering your data...',
    'Setting up your environment...',
    'Preparing your dashboard...',
    'Almost ready...',
    'Welcome back!'
  ];

  // Load Tawk.to chat widget for all visitors on login page
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') return;
    const existingScript = document.querySelector('script[src*="tawk.to"]');
    if (existingScript) return;

    window.Tawk_API = window.Tawk_API || {};
    window.Tawk_LoadStart = new Date();
    window.Tawk_API.onLoad = () => {
      if (window.Tawk_API && window.Tawk_API.minimize) {
        window.Tawk_API.minimize();
      }
    };

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://embed.tawk.to/67f52af29b5f671906ebc6a3/1ioaqvhsq';
    script.charset = 'UTF-8';
    script.setAttribute('crossorigin', '*');
    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    }
  }, []);

  // Generate floating background elements
  useEffect(() => {
    const elements = Array.from({ length: 6 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 60 + 20,
      delay: Math.random() * 2
    }));
    setFloatingElements(elements);
  }, []);

  // Validate form
  useEffect(() => {
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);
    const passwordValid = formData.password.length >= 6;
    setIsFormValid(emailValid && passwordValid);
  }, [formData]);

  // Handle rolling text and redirect
  useEffect(() => {
    if (loginSuccess && redirectStep < rollingMessages.length) {
      const timer = setTimeout(() => {
        if (redirectStep === rollingMessages.length - 1) {
          // Last message - redirect immediately
          redirectToDashboard();
        } else {
          setRedirectStep(redirectStep + 1);
        }
      }, redirectStep === 0 ? 600 : redirectStep === rollingMessages.length - 1 ? 300 : 400);

      return () => clearTimeout(timer);
    }
  }, [loginSuccess, redirectStep]);

  const redirectToDashboard = () => {
    // Option 1: If using React Router
    // navigate('/dashboard');
    
    // Option 2: If using window.location
    window.location.href = '/dashboard';
    
    // Option 3: For demonstration - replace with your actual dashboard URL
    // console.log('🚀 Redirecting to dashboard...');
    
    // For demo purposes, let's just reset the form after a brief delay
    // setTimeout(() => {
    //   setLoginSuccess(false);
    //   setRedirectStep(0);
    //   setFormData({ email: '', password: '' });
    // }, 500);
  };

  // Handle Enter key press
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (isFormValid && !isLoading && !loginSuccess) {
        handleSubmit();
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Block HTML tags and script injection
    if (/<[^>]*>/g.test(value)) {
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Clear login error when user types
    if (loginError) {
      setLoginError('');
    }
  };

  const validateField = (name, value) => {
    let error = '';
    if (name === 'email') {
      if (!value) error = 'Email is required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) error = 'Invalid email format';
    } else if (name === 'password') {
      if (!value) error = 'Password is required';
      else if (value.length < 6) error = 'Password must be at least 6 characters';
    }
    return error;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    if (error) {
      setErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
    setFocusedField('');
  };

  const handleSubmit = async () => {
    // Validate all fields
    const newErrors = {};
    Object.keys(formData).forEach(key => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    setLoginError('');
    
    try {

      // Use environment variable for API URL
      const apiUrl = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';
      const loginUrl = `${apiUrl}/api/auth/login`;
      

      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Important for session cookies
        body: JSON.stringify({
          businessEmail: formData.email,
          password: formData.password
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Login successful
        
        // Store user data if needed
        if (data.user) {
          localStorage.setItem('user', JSON.stringify(data.user));
        }
        if (data.token) {
          localStorage.setItem('token', data.token);
        }
        
        setLoginSuccess(true);
        setRedirectStep(0);
        // Rolling text will start automatically via useEffect
        
      } else {
        // Login failed
        console.error('❌ Login failed:', data);
        setLoginError(data.message || 'Login failed. Please try again.');
        
        // Handle specific error cases
        if (data.message && data.message.includes('verify your email')) {
          setLoginError('Please verify your email address before logging in. Check your inbox for the verification email.');
        } else if (data.message && data.message.includes('Invalid email or password')) {
          setLoginError('Incorrect email or password. Please check and try again.');
        } else if (data.message && data.message.includes('restricted')) {
          setLoginError('Your account has been restricted. Please contact support.');
        }
      }
      
    } catch (error) {
      console.error('❌ Login error:', error);
      setLoginError('We are under maintenance. We will be live soon.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Google Login
  // Google OAuth login handler — verifies credential and creates session
  const handleGoogleSuccess = async (credentialResponse) => {
    setIsLoading(true);
    setLoginError('');

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';
      const response = await fetch(`${apiUrl}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ credential: credentialResponse.credential })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setLoginSuccess(true);
        setRedirectStep(1);
        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.setItem('isAuthenticated', 'true');

        const redirectTo = data.redirectTo || '/dashboard';

        setTimeout(() => setRedirectStep(2), 800);
        setTimeout(() => setRedirectStep(3), 1600);
        setTimeout(() => {
          window.location.href = redirectTo;
        }, 2400);
      } else {
        setLoginError(data.message || 'Google login failed. Please try again.');
      }
    } catch (err) {
      console.error('Google login error:', err);
      setLoginError('Network error during Google login. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Forgot Password
  const handleForgotPassword = (e) => {
    e.preventDefault();
    setShowForgotModal(true);
    setForgotEmail('');
    setForgotError('');
    setForgotFocusedField('');
  };

  const handleForgotSubmit = async () => {
    if (!forgotEmail) {
      setForgotError('Email is required');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(forgotEmail)) {
      setForgotError('Please enter a valid email address');
      return;
    }

    setIsForgotLoading(true);
    setForgotError('');

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';
      const forgotUrl = `${apiUrl}/api/forgetpassword`;

      const response = await fetch(forgotUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: forgotEmail
        })
      });

      const data = await response.json();

      if (response.ok) {
        // Store the ethereal URL if it exists
        if (data.etherealPreviewUrl) {
          setEtherealUrl(data.etherealPreviewUrl);
        }
        
        // Success - close forgot modal and show success modal
        setShowForgotModal(false);
        setShowSuccessModal(true);
      } else {
        setForgotError(data.message || 'Failed to send reset email. Please try again.');
      }
    } catch (error) {
      console.error('❌ Forgot password error:', error);
      setForgotError('Network error. Please check your connection and try again.');
    } finally {
      setIsForgotLoading(false);
    }
  };

  const handleOpenEmail = () => {
    // Try to open the default email client
    window.location.href = 'mailto:';
  };

  // Handle signup navigation
 const handleSignupClick = (e) => {
    e.preventDefault();
    setIsTransitioning(true);
    
    // Smooth transition effect
    setTimeout(() => {
      // Option 1: If using React Router (uncomment if you have react-router-dom)
      // navigate('/signup');
      
      // Option 2: Using window.location (standard navigation)
      window.location.href = '/signup';
      
      // Option 3: If signup is on a different domain/subdomain
      // window.location.href = 'https://yourdomain.com/signup';
      
      // Note: setIsTransitioning(false) is not needed here since we're navigating away
    }, 800);
  };

  const getInputClassName = (fieldName) => {
    const baseClass = "w-full pl-14 pr-12 py-3.5 border-2 rounded-xl transition-all duration-300 text-gray-700 dark:text-slate-100 placeholder-gray-400 dark:placeholder-slate-500 bg-white/80 dark:bg-slate-700/80 backdrop-blur-sm text-sm";

    if (errors[fieldName]) {
      return `${baseClass} border-red-400 focus:border-red-500 focus:ring-4 focus:ring-red-100 dark:focus:ring-red-900`;
    } else if (focusedField === fieldName) {
      return `${baseClass} border-blue-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:focus:ring-blue-900 transform scale-[1.02]`;
    } else if (formData[fieldName]) {
      return `${baseClass} border-green-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:focus:ring-blue-900`;
    }
    return `${baseClass} border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500 focus:border-blue-400 focus:ring-4 focus:ring-blue-50 dark:focus:ring-blue-900`;
  };

  // Forgot password input styling (matching main form)
  const getForgotInputClassName = () => {
    const baseClass = "w-full pl-14 pr-12 py-3.5 border-2 rounded-xl transition-all duration-300 text-gray-700 placeholder-gray-400 bg-white/80 backdrop-blur-sm text-sm";
    
    if (forgotError) {
      return `${baseClass} border-red-400 focus:border-red-500 focus:ring-4 focus:ring-red-100`;
    } else if (forgotFocusedField === 'email') {
      return `${baseClass} border-blue-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transform scale-[1.02]`;
    } else if (forgotEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(forgotEmail)) {
      return `${baseClass} border-green-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100`;
    }
    return `${baseClass} border-gray-200 hover:border-gray-300 focus:border-blue-400 focus:ring-4 focus:ring-blue-50`;
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 flex items-center justify-center p-4 relative overflow-hidden transition-all duration-800 ${isTransitioning ? 'scale-95 opacity-50' : 'scale-100 opacity-100'}`}>
      {/* Animated Background Elements */}
      {floatingElements.map((element) => (
        <div
          key={element.id}
          className="absolute rounded-full bg-gradient-to-r from-blue-200/20 to-blue-200/20 animate-pulse"
          style={{
            left: `${element.x}%`,
            top: `${element.y}%`,
            width: `${element.size}px`,
            height: `${element.size}px`,
            animationDelay: `${element.delay}s`,
            animationDuration: '4s'
          }}
        />
      ))}

      {/* Transition Overlay */}
      <div className={`fixed inset-0 bg-gradient-to-br from-blue-500/20 to-blue-500/20 backdrop-blur-sm z-50 transition-all duration-800 ${isTransitioning ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
        <div className="flex items-center justify-center h-full">
          <div className="bg-white/90 backdrop-blur-xl rounded-2xl p-8 shadow-2xl">
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-lg font-semibold text-gray-700">Navigating to signup...</span>
            </div>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal - Matching Main Form Design */}
      {showForgotModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-50 flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-200 p-8 w-full max-w-md transform transition-all duration-500 scale-100 animate-slideUp relative">
            
            {/* Floating elements for modal background */}
            <div className="absolute top-4 right-12 w-8 h-8 bg-gradient-to-r from-blue-200/30 to-blue-200/30 rounded-full animate-pulse"></div>
            <div className="absolute bottom-6 left-8 w-6 h-6 bg-gradient-to-r from-purple-200/30 to-pink-200/30 rounded-full animate-pulse" style={{animationDelay: '1s'}}></div>

            {/* Modal Header with Logo */}
            <div className="text-center mb-8 relative">
              {/* Close button */}
              <button
                onClick={() => setShowForgotModal(false)}
                className="absolute -top-2 -right-2 p-2 hover:bg-gray-100 rounded-xl transition-all duration-200 group z-10"
              >
                <X className="h-5 w-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
              </button>

              {/* Logo matching main form */}
              <div className="inline-flex items-center justify-center relative mb-4">
                <div className={`w-32 h-12 transition-all duration-500 relative ${
                  forgotFocusedField ? 'scale-110' : ''
                }`}>
                  <img
                    src={isDark ? "/MARS-White.png" : "/MARS-Color.png"}
                    alt="MARS Logo"
                    className="w-full h-full object-contain drop-shadow-lg"
                  />
                </div>
                
                {/* Animated ring around logo */}
                <div className={`absolute inset-0 border-0 border-blue-300 rounded-full transition-all duration-1000 ${
                  forgotFocusedField ? 'scale-150 opacity-0' : 'scale-100 opacity-100'
                }`} />
              </div>
              
              <h3 className="text-xl font-bold text-gray-900 mb-2">Reset Your Password</h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                Enter your email address and we'll send you a secure link to reset your password.
              </p>
              
              {/* Powered by AI badge matching main form */}
              <div className="flex items-center justify-center">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-slate-100 to-gray-100 border border-gray-200/50">
                  <span className="text-sm">🔒</span>
                  <span className="text-xs font-medium text-gray-600">
                    Secure Reset
                  </span>
                </div>
              </div>
            </div>

            {/* Modal Content */}
            <div className="space-y-5">
              {/* Enhanced Email Input - Matching Main Form Style */}
              <div className="relative group">
                <label className="block text-sm font-medium text-gray-700 mb-2 transition-colors duration-200">
                  Email Address
                </label>
                <div className="relative">
                  {/* Enhanced Email Icon - Matching Main Form */}
                  <div className={`absolute left-3 top-1/2 transform -translate-y-1/2 z-10 transition-all duration-300 ${
                    forgotFocusedField === 'email' ? 'scale-110' : ''
                  }`}>
                    <div className={`flex items-center justify-center w-7 h-7 rounded-lg transition-all duration-300 ${
                      forgotFocusedField === 'email' ? 'bg-blue-100 shadow-sm' : 
                      forgotError ? 'bg-red-100' :
                      forgotEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(forgotEmail) ? 'bg-green-100' : 'bg-gray-100'
                    }`}>
                      <svg 
                        className={`w-4 h-4 transition-all duration-300 ${
                          forgotFocusedField === 'email' ? 'text-blue-600' : 
                          forgotError ? 'text-red-600' :
                          forgotEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(forgotEmail) ? 'text-green-600' : 'text-gray-500'
                        }`}
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                        <polyline points="22,6 12,13 2,6"/>
                      </svg>
                    </div>
                  </div>
                  
                  <input
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => {
                      setForgotEmail(e.target.value);
                      if (forgotError) setForgotError('');
                    }}
                    onFocus={() => setForgotFocusedField('email')}
                    onBlur={() => setForgotFocusedField('')}
                    placeholder="Enter your registered email address"
                    className={getForgotInputClassName()}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !isForgotLoading) {
                        handleForgotSubmit();
                      }
                    }}
                  />
                  
                  {/* Success indicator */}
                  {forgotEmail && !forgotError && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(forgotEmail) && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="flex items-center justify-center w-5 h-5 bg-green-100 rounded-full">
                        <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    </div>
                  )}
                  
                  {/* Error indicator */}
                  {forgotError && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="flex items-center justify-center w-5 h-5 bg-red-100 rounded-full">
                        <svg className="w-3 h-3 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <circle cx="12" cy="12" r="10"/>
                          <line x1="12" y1="8" x2="12" y2="12"/>
                          <line x1="12" y1="16" x2="12.01" y2="16"/>
                        </svg>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Error message */}
                {forgotError && (
                  <p className="mt-2 text-xs text-red-500 animate-slideDown flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {forgotError}
                  </p>
                )}
              </div>

              {/* Security Notice */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center w-5 h-5 bg-blue-100 rounded-full mt-0.5">
                    <Shield className="h-3 w-3 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-blue-800 mb-1">Security Notice</p>
                    <p className="text-xs text-blue-700 leading-relaxed">
                      For your security, the reset link will expire in 15 minutes and can only be used once.
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons - Professional & Slim */}
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => setShowForgotModal(false)}
                  className="flex-1 py-2.5 px-4 border border-gray-300 rounded-lg text-gray-600 font-medium text-sm hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 hover:shadow-sm active:scale-98"
                >
                  Cancel
                </button>
                <button
                  onClick={handleForgotSubmit}
                  disabled={isForgotLoading || !forgotEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(forgotEmail)}
                  className={`flex-2 py-2.5 px-5 rounded-lg font-medium text-white text-sm transition-all duration-300 flex items-center justify-center gap-2 relative overflow-hidden group ${
                    isForgotLoading || !forgotEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(forgotEmail)
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 hover:shadow-md active:scale-98'
                  }`}
                >
                  {/* Subtle animated background overlay */}
                  <div className={`absolute inset-0 transition-all duration-500 ${
                    'bg-gradient-to-r from-blue-500 to-blue-600 opacity-0 group-hover:opacity-20'
                  }`} />
                  
                  <div className="relative flex items-center justify-center gap-2">
                    {isForgotLoading ? (
                      <>
                        <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <Send className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform duration-200" />
                        <span>Send Reset Link</span>
                      </>
                    )}
                  </div>
                </button>
              </div>
              
              {/* Form validation indicator - Slim & Professional */}
              <div className="text-center">
                <div className={`inline-flex items-center gap-1.5 text-xs transition-all duration-300 ${
                  forgotEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(forgotEmail) ? 'text-blue-600' : 'text-gray-500'
                }`}>
                  <div className={`w-1 h-1 rounded-full transition-all duration-300 ${
                    forgotEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(forgotEmail) ? 'bg-blue-600' : 'bg-gray-400'
                  }`} />
                  <span className="font-medium">
                    {forgotEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(forgotEmail) ? 'Ready to send' : 'Enter valid email'}
                  </span>
                </div>
              </div>
              
              {/* Help Text - Streamlined */}
              <div className="text-center pt-3 border-t border-gray-100">
                <p className="text-xs text-gray-500">
                  Remember your password?{' '}
                  <button 
                    onClick={() => setShowForgotModal(false)}
                    className="text-blue-600 hover:text-blue-700 font-medium hover:underline transition-colors duration-200"
                  >
                    Sign In
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl p-8 w-full max-w-md transform transition-all duration-300 scale-100">
            {/* Success Icon */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Email Sent!</h3>
              <p className="text-sm text-gray-600">
                We've sent a password reset link to <strong>{forgotEmail}</strong>. 
                Please check your inbox and click the link to reset your password.
              </p>
            </div>

            {/* Action Buttons - Professional & Slim */}
            <div className="flex flex-col gap-3">
              <div className="flex gap-2">
                <button
                  onClick={() => setShowSuccessModal(false)}
                  className="flex-1 py-2.5 px-4 border border-gray-300 rounded-lg text-gray-600 font-medium text-sm hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 hover:shadow-sm active:scale-98"
                >
                  Close
                </button>
                <button
                  onClick={handleOpenEmail}
                  className="flex-2 py-2.5 px-5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium rounded-lg transition-all duration-200 hover:shadow-md active:scale-98 flex items-center justify-center gap-2 relative overflow-hidden group"
                >
                  {/* Subtle animated background overlay */}
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-blue-600 opacity-0 group-hover:opacity-20 transition-all duration-500" />
                  <div className="relative flex items-center justify-center gap-2">
                    <Mail className="h-3.5 w-3.5" />
                    <span>Open Email</span>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Container */}
      <div className="w-full max-w-md relative z-10">
        
        {/* Login Card */}
        <div className={`bg-white/95 dark:bg-slate-800/95 backdrop-blur-xl rounded-3xl shadow-2xl p-8 transition-all duration-500 hover:shadow-3xl relative`}>
          
          {/* Logo and Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center relative">
              {/* Logo Image Container */}
              <div className={`w-40 h-16 transition-all duration-500 relative ${
                focusedField ? 'scale-110' : ''
              }`}>
                  <img
                    src={isDark ? "/MARS-White.png" : "/MARS-Color.png"}
                    alt="MARS Logo"
                    className="w-full h-full object-contain drop-shadow-lg"
                  />
              </div>
              
              {/* Animated ring around logo */}
              <div className={`absolute inset-0 border-0 border-blue-300 rounded-full transition-all duration-1000 ${
                focusedField ? 'scale-150 opacity-0' : 'scale-100 opacity-100'
              }`} />
            </div>
            
            <p className="text-gray-600 dark:text-slate-300 mb-4 text-sm">Sign in to your MARS account</p>
            
            {/* Simple "Powered by AI" capsule */}
            <div className="flex items-center justify-center">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-slate-100 to-gray-100 dark:from-slate-700 dark:to-slate-600 border border-gray-200/50 dark:border-slate-600">
                <span className="text-lg">⚡</span>
                <span className="text-xs font-medium text-gray-600 dark:text-slate-300">
                  Powered by AI
                </span>
              </div>
            </div>
          </div>

          {/* Login Error Message */}
          {loginError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0" />
                <p className="text-sm text-red-700">{loginError}</p>
              </div>
            </div>
          )}

          {/* Login Form */}
          <div className="space-y-5">
            
            {/* Email Field */}
            <div className="relative group">
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-2 transition-colors duration-200">
                Email Address
              </label>
              <div className="relative">
                {/* Email Icon with enhanced styling */}
                <div className={`absolute left-3 top-1/2 transform -translate-y-1/2 z-10 transition-all duration-300 ${
                  focusedField === 'email' ? 'scale-110' : ''
                }`}>
                  <div className={`flex items-center justify-center w-7 h-7 rounded-lg transition-all duration-300 ${
                    focusedField === 'email' ? 'bg-blue-100 shadow-sm' : 
                    errors.email ? 'bg-red-100' :
                    formData.email ? 'bg-green-100' : 'bg-gray-100'
                  }`}>
                    <svg 
                      className={`w-4 h-4 transition-all duration-300 ${
                        focusedField === 'email' ? 'text-blue-600' : 
                        errors.email ? 'text-red-600' :
                        formData.email ? 'text-green-600' : 'text-gray-500'
                      }`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                      <polyline points="22,6 12,13 2,6"/>
                    </svg>
                  </div>
                </div>
                
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  onFocus={() => setFocusedField('email')}
                  onBlur={handleBlur}
                  onKeyDown={handleKeyDown}
                  placeholder="Enter your email address"
                  className={getInputClassName('email')}
                />
                
                {/* Success indicator */}
                {formData.email && !errors.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="flex items-center justify-center w-5 h-5 bg-green-100 rounded-full">
                      <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                )}
                
                {/* Error indicator */}
                {errors.email && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="flex items-center justify-center w-5 h-5 bg-red-100 rounded-full">
                      <svg className="w-3 h-3 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="12" y1="8" x2="12" y2="12"/>
                        <line x1="12" y1="16" x2="12.01" y2="16"/>
                      </svg>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Error message */}
              {errors.email && (
                <p className="mt-2 text-xs text-red-500 animate-slideDown flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.email}
                </p>
              )}
            </div>

            {/* Password Field */}
            <div className="relative group">
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-2 transition-colors duration-200">
                Password
              </label>
              <div className="relative">
                {/* Password Icon with enhanced styling */}
                <div className={`absolute left-3 top-1/2 transform -translate-y-1/2 z-10 transition-all duration-300 ${
                  focusedField === 'password' ? 'scale-110' : ''
                }`}>
                  <div className={`flex items-center justify-center w-7 h-7 rounded-lg transition-all duration-300 ${
                    focusedField === 'password' ? 'bg-blue-100 shadow-sm' : 
                    errors.password ? 'bg-red-100' :
                    formData.password ? 'bg-green-100' : 'bg-gray-100'
                  }`}>
                    <svg 
                      className={`w-4 h-4 transition-all duration-300 ${
                        focusedField === 'password' ? 'text-blue-600' : 
                        errors.password ? 'text-red-600' :
                        formData.password ? 'text-green-600' : 'text-gray-500'
                      }`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                      <circle cx="12" cy="16" r="1"/>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                    </svg>
                  </div>
                </div>
                
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  onFocus={() => setFocusedField('password')}
                  onBlur={handleBlur}
                  onKeyDown={handleKeyDown}
                  placeholder="Enter your password"
                  className={getInputClassName('password')}
                />
                
                {/* Password toggle with enhanced styling */}
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
                  {/* Success indicator for password */}
                  {formData.password && !errors.password && formData.password.length >= 6 && !showPassword && (
                    <div className="flex items-center justify-center w-5 h-5 bg-green-100 rounded-full">
                      <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                  
                  {/* Password visibility toggle */}
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="flex items-center justify-center w-7 h-7 rounded-lg hover:bg-gray-100 transition-all duration-200 group"
                  >
                    <svg 
                      className={`w-4 h-4 transition-all duration-200 ${
                        showPassword ? 'text-blue-600' : 'text-gray-500'
                      } group-hover:text-gray-700 group-hover:scale-110`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      {showPassword ? (
                        <>
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                          <line x1="1" y1="1" x2="23" y2="23"/>
                        </>
                      ) : (
                        <>
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                          <circle cx="12" cy="12" r="3"/>
                        </>
                      )}
                    </svg>
                  </button>
                </div>
              </div>
              
              {/* Error message */}
              {errors.password && (
                <p className="mt-2 text-xs text-red-500 animate-slideDown flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.password}
                </p>
              )}
            </div>

            {/* Forgot Password */}
            <div className="text-right">
              <button 
                onClick={handleForgotPassword}
                className="text-xs text-blue-600 hover:text-blue-700 font-medium transition-all duration-200 hover:underline"
              >
                Forgot password?
              </button>
            </div>

            {/* Login Button with Rolling Text */}
            <button
              onClick={handleSubmit}
              disabled={(!isFormValid && !loginSuccess) || isLoading}
              className={`w-full py-3.5 px-6 rounded-xl font-medium text-white transition-all duration-700 relative overflow-hidden group transform text-sm ${
                loginSuccess
                  ? 'bg-gradient-to-r from-blue-500 to-blue-600 shadow-lg'
                  : !isFormValid || isLoading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg hover:shadow-xl active:scale-95 hover:scale-105'
              }`}
            >
              {/* Animated background overlay */}
              <div className={`absolute inset-0 transition-all duration-700 ${
                loginSuccess 
                  ? 'bg-gradient-to-r from-blue-400 to-blue-500 opacity-100'
                  : 'bg-gradient-to-r from-blue-500 to-blue-600 opacity-0 group-hover:opacity-100'
              }`} />
              
              {/* Button content with rolling text */}
              <div className="relative flex items-center justify-center gap-3 h-5">
                {/* Loading state */}
                {isLoading && !loginSuccess && (
                  <div className="flex items-center justify-center gap-3">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Signing In...</span>
                  </div>
                )}

                {/* Success state with rolling text */}
                {loginSuccess && (
                  <div className="flex items-center justify-center gap-3">
                    <div className="flex items-center justify-center w-5 h-5 bg-white/20 rounded-full">
                      <CheckCircle className="w-3 h-3 text-white" />
                    </div>
                    <span className="font-medium">
                      {rollingMessages[redirectStep]}
                    </span>
                    {redirectStep < rollingMessages.length - 1 && (
                      <div className="flex space-x-1">
                        <div className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                        <div className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                        <div className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                      </div>
                    )}
                  </div>
                )}

                {/* Default state */}
                {!isLoading && !loginSuccess && (
                  <div className="flex items-center justify-center gap-3">
                    <span>Sign In</span>
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-200" />
                  </div>
                )}
              </div>
            </button>

            {/* Google Sign-In — uncomment to enable
            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200 dark:border-slate-700"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-3 bg-white dark:bg-slate-900 text-gray-400 dark:text-slate-500">or continue with</span>
              </div>
            </div>
            <div className="flex justify-center">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => setLoginError('Google login failed. Please try again.')}
                theme={isDark ? 'filled_black' : 'outline'}
                size="large"
                width="100%"
                text="signin_with"
                shape="rectangular"
              />
            </div>
            */}
          </div>

          {/* Footer */}
          <div className="mt-6 text-center border-t border-gray-100 dark:border-slate-700 pt-5">
            <p className="text-xs text-gray-600 dark:text-slate-400">
              Don't have an account?{' '}
              <button 
                onClick={handleSignupClick}
                className="text-blue-600 hover:text-blue-700 font-medium transition-all duration-300 hover:underline relative group"
              >
                <span className="relative z-10">Sign up now</span>
                <span className="absolute inset-0 bg-gradient-to-r from-blue-100 to-blue-100 rounded-md scale-0 group-hover:scale-100 transition-transform duration-300 -m-1"></span>
              </button>
            </p>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-slideDown {
          animation: slideDown 0.3s ease-out;
        }
        
        .shadow-3xl {
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        
        .animate-slideUp {
          animation: slideUp 0.4s ease-out;
        }
      `}</style>
    </div>
  );
};

export default MarsLoginForm;